/*
 * New to Altenew signup band — keep the Klaviyo embed box the same size as the
 * step Klaviyo is actually showing.  ClickUp 86d3ytfw0.
 *
 * HEIGHT.  Klaviyo renders the embed's contents into a wrapper that carries no
 * height of its own, so `.klaviyo-form-<id>` sits at whatever the stylesheet
 * reserves — 54px, the height of the email field the band opens with.  That is
 * fine until the form advances: the SMS step is a phone field, a six-line
 * consent paragraph and two buttons, ~429px, and every pixel past the first 54
 * hangs outside the box.  Whatever follows in the band (the disclaimer) is laid
 * out against the 54px box and paints over the overflow, so the phone field,
 * SIGN UP and NO THANKS are all covered — document.elementsFromPoint() over the
 * phone field returned the disclaimer's <p> first, which is why nothing on the
 * step could be clicked (reported by Allyssa, 08/17).
 *
 * WIDTH.  The same wrapper leaks sideways for the same reason.  Klaviyo writes
 * the widths its own form designer produced straight onto the elements as inline
 * style — the <form> ships `max-width: 600px` — and those are desktop numbers.
 * The mobile band gives the box 283px at a 390px viewport (measured: 390 less
 * the 15px scrollbar, the section's 20px gutters and the band's 24px inset), so
 * anything Klaviyo sized against its own design, or any flex row it laid out
 * `nowrap`, is wider than the box it is sitting in and spills past the gold.
 * A stylesheet cannot answer this: inline style outranks it, the elements
 * carrying the widths are Klaviyo's and unnamed, and which of them overflows
 * changes with the step.
 *
 * Both are the same job — the size is Klaviyo's to decide and it changes as the
 * form moves between steps, so there is no static number the stylesheet could
 * reserve.  Measure the rendered form instead and mirror it onto the box.
 *
 * The width pass only touches an element that is *actually* wider than the box,
 * so a step that already fits is left exactly as Klaviyo drew it — this is a
 * clamp, not a re-layout.  `max-width: 100%` is what does the clamping: written
 * inline it ties with Klaviyo's own inline `width`, and max-width wins that tie
 * on its own.  A flex row only gets `wrap` when its contents genuinely overflow
 * it, which is what stops a row from pushing out sideways once its items can no
 * longer shrink.
 *
 * The ResizeObserver watches the <form>, never the box — watching the box while
 * writing its min-height is a feedback loop.  Writing style attributes cannot
 * re-trigger the MutationObserver either, since it is childList-only.  The
 * clamp converges: once an element is inside the box it stops matching, so the
 * resize the clamp itself causes settles on the next pass.
 *
 * The MutationObserver is what survives a step change, since Klaviyo replaces
 * the <form> wholesale and the fresh one arrives carrying its inline widths
 * again.
 */
(function () {
  if (window.__ntaSignupKlaviyoSize) return;
  window.__ntaSignupKlaviyoSize = true;

  // The footer newsletter band carries the same embed since ClickUp 86d4bdfnm,
  // and has the same problem: a 54px box under a ~429px SMS step, with the
  // footer links laid out against the 54px.
  var BOX =
    ".nta-signup__form [class^='klaviyo-form-'], " +
    ".footer__signup--klaviyo [class^='klaviyo-form-']";
  var RESERVED = 54;

  // Sub-pixel slack. Layout widths are fractional (the box measures 282.667px at
  // a 390px viewport), so an exact `>` comparison would clamp elements that fit.
  var SLACK = 1;

  // Undo this pass's own work before measuring the next one. Without it the
  // clamp is one-way: a row wrapped in portrait would stay wrapped after a
  // rotate back to landscape, where it fits on one line again. Only the
  // elements this script marked are touched — Klaviyo's own inline style is
  // never read back or rewritten.
  function reset(box) {
    var marked = box.querySelectorAll('[data-nta-clamped]');
    for (var i = 0; i < marked.length; i++) {
      var el = marked[i];
      el.style.maxWidth = '';
      el.style.boxSizing = '';
      el.style.flexWrap = '';
      el.removeAttribute('data-nta-clamped');
    }
  }

  function clamp(el, limit) {
    if (el.getBoundingClientRect().width > limit + SLACK) {
      el.style.maxWidth = '100%';
      el.style.boxSizing = 'border-box';
      el.setAttribute('data-nta-clamped', '');
    }

    // An element can sit inside the box and still overflow itself — a flex row
    // whose items have stopped shrinking is the case that matters here. Only
    // relax the ones Klaviyo laid out `nowrap`; anything already wrapping, or
    // not a flex container, is overflowing for some other reason and is not
    // ours to rearrange.
    if (el.scrollWidth > el.clientWidth + SLACK) {
      var style = window.getComputedStyle(el);
      if (
        (style.display === 'flex' || style.display === 'inline-flex') &&
        style.flexWrap === 'nowrap'
      ) {
        el.style.flexWrap = 'wrap';
        el.setAttribute('data-nta-clamped', '');
      }
    }
  }

  // Width first, height second, and not the other way round: wrapping a row
  // makes the form taller, so a height measured before the clamp would be the
  // height of a layout that is about to change.
  function sync(box) {
    var form = box.querySelector('form');

    if (form) {
      reset(box);

      // clientWidth, not the bounding rect: the box's own content width is what
      // Klaviyo's markup has to fit inside. Setting the box's min-height below
      // cannot change it, so it stays true for the whole pass.
      var limit = box.clientWidth;
      if (limit) {
        clamp(form, limit);
        var nodes = form.querySelectorAll('*');
        for (var i = 0; i < nodes.length; i++) clamp(nodes[i], limit);
      }
    }

    var height = form ? form.getBoundingClientRect().height : 0;
    // Below the reserved height the stylesheet's own min-height is the better
    // answer — it holds the box open while Klaviyo is still loading.
    box.style.minHeight = height > RESERVED ? Math.ceil(height) + 'px' : '';
  }

  function watch(box) {
    if (box.dataset.ntaSizeWatched) return;
    box.dataset.ntaSizeWatched = '1';

    var sizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(function () {
            sync(box);
          })
        : null;
    var watched = null;

    function attach() {
      var form = box.querySelector('form');
      if (form === watched) return;
      if (sizeObserver) {
        if (watched) sizeObserver.unobserve(watched);
        if (form) sizeObserver.observe(form);
      }
      watched = form;
    }

    new MutationObserver(function () {
      attach();
      sync(box);
    }).observe(box, { childList: true, subtree: true });

    attach();
    sync(box);
  }

  function scan() {
    var boxes = document.querySelectorAll(BOX);
    for (var i = 0; i < boxes.length; i++) watch(boxes[i]);
  }

  // `watch` is a no-op on a box it has already seen, so a resize has to ask for
  // the measurement itself. The ResizeObserver does not cover this: it watches
  // the <form>, and the form only stops fitting once the box around it has
  // narrowed — the box is what the viewport changed.
  function resync() {
    var boxes = document.querySelectorAll(BOX);
    for (var i = 0; i < boxes.length; i++) sync(boxes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // A rotate or a resize changes the box, not the form, so nothing else here
  // would notice. Debounced with rAF: resize fires per frame while dragging and
  // every pass forces layout.
  var pending = false;
  window.addEventListener('resize', function () {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(function () {
      pending = false;
      resync();
    });
  });

  // The band is a section, so the theme editor can add or move one after load.
  document.addEventListener('shopify:section:load', scan);
})();
